import logo from "../logo.png";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const FEATURES = [
  {
    icon: "🧠",
    title: "Smart suggestions",
    desc: "AI picks what fits your child's taste, allergies, and what they just ate — no repeats."
  },
  {
    icon: "⏱️",
    title: "30-second decisions",
    desc: "Answer a few quick questions and get personalised dish ideas instantly."
  },
  {
    icon: "🍽️",
    title: "Recipes + dish photos",
    desc: "Premium members get full recipes and a generated photo so kids can see what's coming."
  }
];

const STEPS = [
  { num: "01", title: "Tell us the vibe", desc: "Pick a cuisine, note allergies, and describe texture preferences." },
  { num: "02", title: "Get dish ideas", desc: "NextBite suggests 5–10 dishes matched to your preferences and location." },
  { num: "03", title: "Dive into a recipe", desc: "Tap any dish to get a full recipe and photo — with one click." }
];

const FAQS = [
  {
    q: "Is NextBite free?",
    a: "Yes. Dish suggestions are free for all users. Recipes and dish photos require a Premium subscription ($3/mo or $24/yr), which includes a 7-day free trial."
  },
  {
    q: "Do I need an account?",
    a: "You can try the app as a guest. Create a free account to save your history and preferences."
  },
  {
    q: "Can I cancel anytime?",
    a: "Absolutely. Cancel from your Profile page — no questions asked, no hidden fees."
  },
  {
    q: "What cuisines are supported?",
    a: "Indian, Italian, Chinese, Mexican, Japanese, and more. You can also enter any custom cuisine."
  }
];

export default function Intro() {
  const nav = useNavigate();

  return (
    <div style={styles.page}>
      <Helmet>
        <title>NextBite — Home</title>
        <meta name="description" content="NextBite helps you decide what to feed your child next — smart, stress-free meal suggestions based on what they just ate." />
        <meta property="og:title" content="NextBite — Stress-free meal ideas for your child" />
        <meta property="og:url" content="https://www.nextbite.ca/" />
        <meta property="og:image" content="https://www.nextbite.ca/og-image.png" />
      </Helmet>

      {/* NAV */}
      <nav style={styles.nav}>
        <div style={styles.navBrand}>
          <img src={logo} alt="NextBite" style={styles.navLogo} />
          <span style={styles.navWordmark}>NextBite</span>
        </div>
        <div style={styles.navActions}>
          <button onClick={() => nav("/login")} style={styles.navLoginBtn}>Login</button>
          <button onClick={() => nav("/register")} style={styles.navRegisterBtn}>Get started free</button>
        </div>
      </nav>

      {/* HERO */}
      <section style={styles.hero}>
        <div style={styles.heroBadge}>✨ Free to try — no account needed</div>
        <h1 style={styles.heroTitle}>
          What should my child<br />eat <span style={styles.heroAccent}>next?</span>
        </h1>
        <p style={styles.heroSubtitle}>
          NextBite takes the daily guesswork out of feeding kids. Get personalised meal ideas in seconds, tailored to taste, texture, and what they last ate.
        </p>
        <div style={styles.heroCtas}>
          <button onClick={() => nav("/home")} style={styles.heroPrimary}>
            Try NextBite — it's free
          </button>
          <button onClick={() => nav("/register")} style={styles.heroSecondary}>
            Create free account
          </button>
        </div>
        <p style={styles.heroNote}>No credit card required · Cancel anytime</p>
      </section>

      {/* FEATURES */}
      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>Why parents love NextBite</p>
        <h2 style={styles.sectionTitle}>Everything you need to end mealtime stress</h2>
        <div style={styles.featureGrid}>
          {FEATURES.map((f) => (
            <div key={f.title} style={styles.featureCard}>
              <div style={styles.featureIcon}>{f.icon}</div>
              <h3 style={styles.featureTitle}>{f.title}</h3>
              <p style={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ ...styles.section, background: "rgba(255,255,255,0.6)", borderRadius: 28, padding: "48px 32px" }}>
        <p style={styles.sectionEyebrow}>How it works</p>
        <h2 style={styles.sectionTitle}>From question to meal in under a minute</h2>
        <div style={styles.stepsGrid}>
          {STEPS.map((s) => (
            <div key={s.num} style={styles.stepCard}>
              <div style={styles.stepNum}>{s.num}</div>
              <h3 style={styles.stepTitle}>{s.title}</h3>
              <p style={styles.stepDesc}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING TEASER */}
      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>Simple pricing</p>
        <h2 style={styles.sectionTitle}>Start free, upgrade when you're ready</h2>
        <div style={styles.pricingRow}>
          <div style={styles.pricingCard}>
            <div style={styles.pricingPlan}>Free</div>
            <div style={styles.pricingPrice}>$0</div>
            <ul style={styles.pricingList}>
              <li>Dish name suggestions</li>
              <li>All cuisines</li>
              <li>Allergy filtering</li>
            </ul>
            <button onClick={() => nav("/home")} style={styles.heroSecondary}>Try for free</button>
          </div>
          <div style={{ ...styles.pricingCard, ...styles.pricingCardPremium }}>
            <div style={styles.pricingBadge}>Most popular</div>
            <div style={styles.pricingPlan}>Premium</div>
            <div style={styles.pricingPrice}>$3<span style={styles.pricingPer}>/mo</span></div>
            <ul style={styles.pricingList}>
              <li>Everything in Free</li>
              <li>Full AI-generated recipes</li>
              <li>Dish photos</li>
              <li>7-day free trial</li>
            </ul>
            <button onClick={() => nav("/upgrade")} style={styles.heroPrimary}>Start free trial</button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>FAQ</p>
        <h2 style={styles.sectionTitle}>Common questions</h2>
        <div style={styles.faqList}>
          {FAQS.map((item) => (
            <div key={item.q} style={styles.faqItem}>
              <p style={styles.faqQ}>{item.q}</p>
              <p style={styles.faqA}>{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section style={styles.ctaSection}>
        <img src={logo} alt="" style={styles.ctaLogo} />
        <h2 style={styles.ctaTitle}>Ready to end mealtime stress?</h2>
        <p style={styles.ctaSubtitle}>Join thousands of parents making meal decisions in seconds.</p>
        <button onClick={() => nav("/home")} style={styles.heroPrimary}>
          Get your first suggestion — free
        </button>
      </section>

      {/* FOOTER */}
      <footer style={styles.footer}>
        <span>© 2026 NextBite</span>
        <span style={styles.footerDot}>·</span>
        <button onClick={() => nav("/privacy-policy")} style={styles.footerLink}>Privacy</button>
        <span style={styles.footerDot}>·</span>
        <button onClick={() => nav("/terms-and-conditions")} style={styles.footerLink}>Terms</button>
        <span style={styles.footerDot}>·</span>
        <button onClick={() => nav("/feedback")} style={styles.footerLink}>Feedback</button>
      </footer>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    fontFamily: "'Inter', system-ui, sans-serif",
    color: "#0f172a",
    background: "transparent"
  },

  /* Nav */
  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    background: "rgba(255,250,247,0.88)",
    backdropFilter: "blur(14px)",
    borderBottom: "1px solid rgba(234,179,160,0.2)",
    position: "sticky",
    top: 0,
    zIndex: 100
  },
  navBrand: { display: "flex", alignItems: "center", gap: 8 },
  navLogo: { width: 32, height: 32, objectFit: "contain" },
  navWordmark: { fontWeight: 700, fontSize: 17, color: "#1a1a1a", letterSpacing: 0.2 },
  navActions: { display: "flex", alignItems: "center", gap: 10 },
  navLoginBtn: {
    padding: "8px 16px",
    borderRadius: 999,
    border: "1px solid #e2e8f0",
    background: "transparent",
    color: "#475569",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer"
  },
  navRegisterBtn: {
    padding: "8px 18px",
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(242,95,76,0.28)"
  },

  /* Hero */
  hero: {
    textAlign: "center",
    padding: "80px 24px 60px",
    maxWidth: 720,
    margin: "0 auto"
  },
  heroBadge: {
    display: "inline-block",
    padding: "6px 16px",
    borderRadius: 999,
    background: "rgba(242,95,76,0.10)",
    color: "#c2400e",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 24,
    letterSpacing: 0.2
  },
  heroTitle: {
    margin: "0 0 20px",
    fontSize: "clamp(36px, 6vw, 64px)",
    fontWeight: 800,
    lineHeight: 1.08,
    color: "#0f172a",
    letterSpacing: "-0.02em"
  },
  heroAccent: {
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text"
  },
  heroSubtitle: {
    margin: "0 auto 32px",
    maxWidth: 560,
    fontSize: 19,
    lineHeight: 1.65,
    color: "#475569"
  },
  heroCtas: {
    display: "flex",
    justifyContent: "center",
    gap: 12,
    flexWrap: "wrap"
  },
  heroPrimary: {
    padding: "14px 28px",
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 12px 28px rgba(242,95,76,0.30)"
  },
  heroSecondary: {
    padding: "14px 28px",
    borderRadius: 999,
    border: "1px solid #f0d9d1",
    background: "rgba(255,255,255,0.88)",
    color: "#7c3d2e",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer"
  },
  heroNote: {
    marginTop: 16,
    marginBottom: 0,
    fontSize: 13,
    color: "#94a3b8"
  },

  /* Sections */
  section: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "56px 24px"
  },
  sectionEyebrow: {
    margin: "0 0 10px",
    fontSize: 13,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#f25f4c"
  },
  sectionTitle: {
    margin: "0 0 36px",
    fontSize: "clamp(24px, 4vw, 36px)",
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.01em"
  },

  /* Features */
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 20
  },
  featureCard: {
    padding: "28px 24px",
    borderRadius: 20,
    background: "rgba(255,255,255,0.84)",
    border: "1px solid rgba(255,255,255,0.72)",
    boxShadow: "0 8px 28px rgba(15,23,42,0.06)",
    backdropFilter: "blur(8px)"
  },
  featureIcon: { fontSize: 36, marginBottom: 14 },
  featureTitle: { margin: "0 0 10px", fontSize: 18, fontWeight: 700, color: "#0f172a" },
  featureDesc: { margin: 0, fontSize: 15, color: "#475569", lineHeight: 1.65 },

  /* Steps */
  stepsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 28
  },
  stepCard: { padding: "4px 0" },
  stepNum: {
    fontSize: 42,
    fontWeight: 900,
    color: "rgba(242,95,76,0.15)",
    lineHeight: 1,
    marginBottom: 10
  },
  stepTitle: { margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#0f172a" },
  stepDesc: { margin: 0, fontSize: 15, color: "#475569", lineHeight: 1.65 },

  /* Pricing */
  pricingRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 20,
    maxWidth: 640,
    margin: "0 auto"
  },
  pricingCard: {
    padding: "32px 28px",
    borderRadius: 22,
    background: "rgba(255,255,255,0.84)",
    border: "1px solid #e2e8f0",
    boxShadow: "0 8px 28px rgba(15,23,42,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    position: "relative"
  },
  pricingCardPremium: {
    border: "2px solid #f25f4c",
    boxShadow: "0 16px 40px rgba(242,95,76,0.14)"
  },
  pricingBadge: {
    position: "absolute",
    top: -12,
    left: "50%",
    transform: "translateX(-50%)",
    padding: "4px 14px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #ff7a59, #f25f4c)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap"
  },
  pricingPlan: { fontSize: 16, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 },
  pricingPrice: { fontSize: 42, fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  pricingPer: { fontSize: 18, fontWeight: 500, color: "#94a3b8" },
  pricingList: {
    margin: 0,
    padding: "0 0 0 20px",
    fontSize: 15,
    color: "#475569",
    lineHeight: 2,
    flexGrow: 1
  },

  /* FAQ */
  faqList: { maxWidth: 680, display: "flex", flexDirection: "column", gap: 0 },
  faqItem: {
    padding: "22px 0",
    borderTop: "1px solid #e2e8f0"
  },
  faqQ: { margin: "0 0 8px", fontSize: 17, fontWeight: 700, color: "#0f172a" },
  faqA: { margin: 0, fontSize: 15, color: "#475569", lineHeight: 1.7 },

  /* Bottom CTA */
  ctaSection: {
    textAlign: "center",
    padding: "72px 24px",
    background: "linear-gradient(145deg, rgba(255,244,240,0.9), rgba(255,255,255,0.8))",
    margin: "40px 24px",
    borderRadius: 28,
    maxWidth: 960,
    marginLeft: "auto",
    marginRight: "auto"
  },
  ctaLogo: { width: 56, height: 56, objectFit: "contain", marginBottom: 20 },
  ctaTitle: { margin: "0 0 14px", fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#0f172a" },
  ctaSubtitle: { margin: "0 0 28px", fontSize: 17, color: "#475569" },

  /* Footer */
  footer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    padding: "28px 24px",
    fontSize: 14,
    color: "#94a3b8",
    borderTop: "1px solid #e2e8f0"
  },
  footerDot: { color: "#cbd5e1" },
  footerLink: {
    border: "none",
    background: "transparent",
    color: "#94a3b8",
    fontSize: 14,
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline"
  }
};
